// Weather utility using Open-Meteo API (free, no API key required)

interface WeatherData {
  temperature: number;
  weatherCode: number;
  city: string;
  icon: string;
  description: string;
  updatedAt: string;
}

const CACHE_KEY = 'fh-weather-data';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// WMO Weather interpretation codes → icon + description
const WMO_CODES: Record<number, { icon: string; description: string }> = {
  0: { icon: '☀️', description: 'Clear sky' },
  1: { icon: '🌤️', description: 'Mainly clear' },
  2: { icon: '⛅', description: 'Partly cloudy' },
  3: { icon: '☁️', description: 'Overcast' },
  45: { icon: '🌫️', description: 'Foggy' },
  48: { icon: '🌫️', description: 'Rime fog' },
  51: { icon: '🌦️', description: 'Light drizzle' },
  53: { icon: '🌦️', description: 'Drizzle' },
  55: { icon: '🌦️', description: 'Heavy drizzle' },
  61: { icon: '🌧️', description: 'Light rain' },
  63: { icon: '🌧️', description: 'Rain' },
  65: { icon: '🌧️', description: 'Heavy rain' },
  66: { icon: '🌧️', description: 'Freezing rain' },
  67: { icon: '🌧️', description: 'Heavy freezing rain' },
  71: { icon: '❄️', description: 'Light snow' },
  73: { icon: '❄️', description: 'Snow' },
  75: { icon: '❄️', description: 'Heavy snow' },
  77: { icon: '❄️', description: 'Snow grains' },
  80: { icon: '🌦️', description: 'Light showers' },
  81: { icon: '🌧️', description: 'Showers' },
  82: { icon: '🌧️', description: 'Heavy showers' },
  85: { icon: '🌨️', description: 'Light snow showers' },
  86: { icon: '🌨️', description: 'Snow showers' },
  95: { icon: '⛈️', description: 'Thunderstorm' },
  96: { icon: '⛈️', description: 'Thunderstorm with hail' },
  99: { icon: '⛈️', description: 'Thunderstorm with heavy hail' },
};

const getWeatherInfo = (code: number) => WMO_CODES[code] || { icon: '🌡️', description: 'Unknown' };

const getCachedWeather = (): WeatherData | null => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data: WeatherData = JSON.parse(raw);
    if (Date.now() - new Date(data.updatedAt).getTime() > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
};

const reverseGeocode = async (lat: number, lon: number): Promise<string> => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=10`,
      { headers: { 'User-Agent': 'FamHearth/1.0' } }
    );
    const data = await res.json();
    return data.address?.city || data.address?.town || data.address?.village || data.address?.county || 'Unknown';
  } catch {
    return 'Unknown';
  }
};

export const fetchWeather = async (): Promise<WeatherData | null> => {
  // Check cache first
  const cached = getCachedWeather();
  if (cached) return cached;

  try {
    // Get user location
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: 10000,
        maximumAge: 600000, // accept 10-min old positions
        enableHighAccuracy: false,
      });
    });

    const { latitude, longitude } = position.coords;

    // Fetch weather and city in parallel
    const [weatherRes, city] = await Promise.all([
      fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=celsius`
      ),
      reverseGeocode(latitude, longitude),
    ]);

    const weatherJson = await weatherRes.json();
    const current = weatherJson.current;
    const info = getWeatherInfo(current.weather_code);

    const data: WeatherData = {
      temperature: Math.round(current.temperature_2m),
      weatherCode: current.weather_code,
      city,
      icon: info.icon,
      description: info.description,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    return data;
  } catch {
    // Geolocation denied or API error — return null silently
    return null;
  }
};

export type { WeatherData };
